import {debounce} from '@github/mini-throttle'
// eslint-disable-next-line no-restricted-imports
import {observe} from '@github/selector-observer'
// eslint-disable-next-line no-restricted-imports
import {on} from 'delegated-events'
import {parseHTML} from '@github-ui/parse-html'
import safeStorage from '@github-ui/safe-storage'
import {showGlobalError} from './behaviors/ajax-error'
import {trackView} from './hydro-tracking'
import type {FeedContainerElement} from './dashboard/feed/feed-container-element'
import type {TopicFeedsContainerElement} from './dashboard/topic-feeds/topic-feeds-container-element'

const {getItem, setItem} = safeStorage('localStorage')
// for persisting which tab was last selected in localstorage
const LastSavedFeedsTabKey = 'feeds-last-saved-tab'
// possible values for the last saved tab, first entry is used as default
const PossibleFeedsTabKeys = ['feed-next', 'feed-original']
const DefaultFeedsTab = PossibleFeedsTabKeys[0]!

on('click', '.js-follow-unfollow-submit', async function (event) {
  const button = event.currentTarget as HTMLButtonElement
  const form = button.form as HTMLFormElement
  event.preventDefault()
  let response
  try {
    response = await fetch(form.action, {
      method: form.method,
      body: new FormData(form),
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    })
  } catch {
    showGlobalError()
  }
  if (button.classList.contains('js-hide-heading-menu-button')) {
    button.hidden = true
  }
  if (response && !response.ok) {
    showGlobalError()
  }
})

observe('.js-feeds-tabs', {
  constructor: HTMLElement,
  initialize(tabsEl) {
    if (tabsEl) {
      let lastStoredTab = getItem(LastSavedFeedsTabKey) || DefaultFeedsTab
      if (PossibleFeedsTabKeys.indexOf(lastStoredTab) === -1) {
        lastStoredTab = DefaultFeedsTab
      }

      const tabButton = tabsEl.querySelector<HTMLButtonElement>(`button#${lastStoredTab}`)

      if (tabButton) {
        tabButton.setAttribute('aria-selected', 'true')
        const controlledPanelId = tabButton.getAttribute('aria-controls') || ''
        const controlledPanel = document.getElementById(controlledPanelId)
        controlledPanel && controlledPanel.removeAttribute('hidden')
      }

      const feedbackLink = document.querySelector('.js-feedback-link')
      if (feedbackLink) {
        feedbackLink.toggleAttribute('hidden', lastStoredTab !== 'feed-next')
      }
    }
  },
})

on('click', '.js-feeds-tabs button', function (e) {
  const button = e.currentTarget as HTMLButtonElement

  if (!button.id || !PossibleFeedsTabKeys.includes(button.id)) return

  // show feedback link if feed-next
  const feedbackLink = document.querySelector('.js-feedback-link')
  if (feedbackLink) {
    feedbackLink.toggleAttribute('hidden', button.id !== 'feed-next')
  }

  setItem(LastSavedFeedsTabKey, button.id)
})

on('click', '.js-toggle-feed-views', function (e: Event) {
  const item = e.currentTarget as HTMLElement
  const name = item?.getAttribute('data-name')
  if (!name) {
    return
  }

  const topicFeedsContainer = document.getElementById('topic-feeds-container') as TopicFeedsContainerElement
  const feedContainer = document.querySelector('feed-container') as FeedContainerElement

  const url = new URL('', window.origin)

  const listItems = item.parentElement?.children
  if (!listItems) {
    return
  }

  for (const li of listItems) {
    li.classList.remove('ActionListItem--navActive')
  }

  if (name === 'topics') {
    feedContainer?.toggleAttribute('hidden', true)
    topicFeedsContainer?.toggleAttribute('hidden', false)

    item?.classList.add('ActionListItem--navActive')
    url.searchParams.append('view', 'topics')
    history.replaceState(null, document.title, url.href)
  } else {
    feedContainer?.toggleAttribute('hidden', false)
    topicFeedsContainer?.toggleAttribute('hidden', true)

    item?.classList.add('ActionListItem--navActive')
    if (name === 'for-you') {
      feedContainer?.handleForYouFeedItemClick()
    } else {
      feedContainer?.handleTopicFeedItemClick(name, item.getAttribute('data-display-name') || '')
    }
  }
})

// feed post topic tag menu
on('click', '.js-tag-menu-topic', function (e) {
  e.preventDefault()
  const topic = e.currentTarget as HTMLElement
  const name = topic?.getAttribute('data-name') || ''
  const id = topic?.getAttribute('data-id') || ''
  if (!name || !id) return

  updateSelectedTopicInForm(name, id)
  closeFeedPostTagMenu()
})

on('click', '.js-feed-post-active-topic-remove', function () {
  updateSelectedTopicInForm(null, null)
})

function updateSelectedTopicInForm(name: string | null, id: string | null) {
  const activeTopicId = document.querySelector('.js-feed-post-form-selected-topic-id') as HTMLInputElement
  const activeTopicName = document.querySelector('.js-feed-post-form-selected-topic-name') as HTMLInputElement
  const topicTagMenuButton = document.querySelector('.js-feed-post-tags-menu-show-button') as HTMLButtonElement
  const displayedTopicTagName = document.querySelector('.js-feed-post-active-tag-name') as HTMLElement
  const displayedTopicTagWrapper = document.querySelector('.js-feed-post-active-tag-wrapper') as HTMLElement

  if (!activeTopicId || !activeTopicName || !topicTagMenuButton || !displayedTopicTagName || !displayedTopicTagWrapper)
    return

  if (id && name) {
    activeTopicId.value = id
    activeTopicName.value = name
    displayedTopicTagName.textContent = name
    displayedTopicTagWrapper.removeAttribute('hidden')
    topicTagMenuButton.setAttribute('hidden', 'true')
  } else {
    activeTopicId.removeAttribute('value')
    activeTopicName.removeAttribute('value')
    displayedTopicTagWrapper.setAttribute('hidden', 'true')
    topicTagMenuButton.removeAttribute('hidden')
  }
}

function closeFeedPostTagMenu() {
  const show_button = document.querySelector('.js-feed-post-tags-menu-show-button') as HTMLButtonElement
  if (!show_button) return
  show_button.click()
}

// feed card visibility tracking
const cards: {[key: string]: ReturnType<typeof setTimeout>} = {}
const visibleThreshold = 0.8 // amnt of the card element in the viewport for it to be considered visible
const minimumReadTime = 1000 // 1 second
const observer = new IntersectionObserver(
  function (entries) {
    for (const entry of entries) {
      const el = entry.target as HTMLElement
      const hydroAttrs = el.getAttribute('data-hydro-view')
      if (!hydroAttrs) return

      const id = JSON.parse(hydroAttrs).payload.feed_card.card_retrieved_id
      const isVisible = entry.intersectionRatio > visibleThreshold

      if (!isVisible && cards[id]) {
        clearTimeout(cards[id])
        delete cards[id]
      }

      if (isVisible && !cards[id]) {
        cards[id] = setTimeout(() => {
          trackView(el)
        }, minimumReadTime)
      }
    }
  },
  {
    rootMargin: '0px',
    threshold: [0, visibleThreshold],
  },
)

observe('.js-feed-item-view', el => observer.observe(el))

// heading menu button animations
let unhiddenHeadingMenus: HTMLDetailsElement[] = []

function getHeadingMenuButton(card: HTMLElement): HTMLDetailsElement | null {
  const menu = card.getElementsByClassName('js-feed-item-heading-menu')
  if (menu.length === 0) return null
  return menu[0] as HTMLDetailsElement
}

const debouncedShowMenuButton = debounce((card: HTMLElement) => {
  const menu = getHeadingMenuButton(card)
  if (!menu) return

  menu.hidden = false
  unhiddenHeadingMenus.push(menu)
}, 100)

const debouncedHideMenuButton = debounce(() => {
  hideAllHeadingMenus()
}, 100)

function hideAllHeadingMenus() {
  const visibleMenus = []
  for (const menu of unhiddenHeadingMenus) {
    // if menu is open, please don't hide it
    const card = menu.closest('.js-feed-item-component') as HTMLElement

    if (menu.open || card.hasAttribute('focus-child')) {
      visibleMenus.push(menu)
    } else {
      menu.hidden = true
    }
  }

  unhiddenHeadingMenus = visibleMenus
}

observe('.js-feed-item-component', e => {
  e.addEventListener('mouseenter', (evt: Event) => {
    const card = evt.target as HTMLElement
    debouncedShowMenuButton(card)
  })
  e.addEventListener('mouseleave', debouncedHideMenuButton)
})

observe('.js-feed-item-component *', e => {
  e.addEventListener('focus', (evt: Event) => {
    const child = evt.target as HTMLElement
    // if child is focused don't hide the menu
    const card = child.closest('.js-feed-item-component') as HTMLElement
    card.setAttribute('focus-child', child.localName)
    debouncedShowMenuButton(card)
  })
  e.addEventListener('blur', (evt: Event) => {
    const child = evt.target as HTMLElement
    const card = child.closest('.js-feed-item-component') as HTMLElement
    card.removeAttribute('focus-child')
    debouncedHideMenuButton()
  })
})

on('click', 'js-feed-item-next-component', function () {
  sessionStorage.setItem('github:dashboard:scrollHeight', `${window.scrollY}`)
})

on('click', '.js-feed-item-heading-menu', e => {
  const template = e.currentTarget.querySelector<HTMLTemplateElement>('template')

  if (!template) return
  const templateContainerContent = template.content
  const target = e.currentTarget.querySelector('.js-block-user-button')
  target?.append(templateContainerContent)
})

on('click', '.js-feed-filter-trigger', function () {
  const menu = document.querySelector<HTMLDetailsElement>('.js-feed-filter-menu')
  if (!menu) return
  menu.open = true
})

async function submitEventVisibility(target: EventTarget & Element): Promise<void | {response: Response}> {
  const form = target.parentElement as HTMLFormElement
  if (!form) return

  const formData = new FormData(form)
  const eventId = formData.get('event_id')
  if (!eventId) return

  let response
  try {
    response = await fetch(form.action, {
      method: form.method,
      body: formData,
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    })
  } catch {
    showGlobalError()
  }
  if (response && !response.ok) {
    showGlobalError()
  } else {
    form.closest('.js-feed-item-heading-menu')?.removeAttribute('open')

    const card = form.closest('.js-feed-item-component')!
    const labelContainer = card.querySelector<HTMLElement>('.js-hidden-label-target')
    if (!labelContainer) return

    const json = await response?.json()
    if (json.hidden) {
      const template = document.querySelector<HTMLTemplateElement>('#hidden-event-label')
      if (!template) return

      const content = template.content.cloneNode(true)
      labelContainer.appendChild(content)
    } else {
      labelContainer.textContent = ''
    }

    const newForm = parseHTML(document, json.form.trim())
    form.replaceWith(newForm)
  }
}

async function submitFeedDisinterest(target: EventTarget & Element): Promise<void | {response: Response}> {
  const form = target.parentElement as HTMLFormElement
  if (!form) return

  const formData = new FormData(form)
  const eventId = formData.get('event_id')
  const reasonDismissed = formData.get('reason_dismissed')
  const cardType = formData.get('card_type')
  if (!eventId) return

  let response
  try {
    response = await fetch(form.action, {
      method: form.method,
      body: formData,
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    })
  } catch {
    showGlobalError()
  }
  if (response && !response.ok) {
    showGlobalError()
  } else {
    if (reasonDismissed === 'dismissed' && cardType === 'create_discussion') {
      removeAnnouncementCard(eventId)
    } else {
      // close the dialog and menu
      toggleDismissedPlaceholder(eventId)
    }
    form.closest('.js-feed-item-disinterest-dialog')?.parentElement?.removeAttribute('open')
    form.closest('.js-feed-item-heading-menu')?.removeAttribute('open')
  }
}

function toggleDismissedPlaceholder(event_id: FormDataEntryValue) {
  const card = document.querySelector(`#feed-item-${event_id}`)
  const showMore = card?.parentElement?.querySelector(':scope > .js-details-target')

  const placeholder = document.querySelector(`#dismissed-item-placeholder-${event_id}`)
  if (!card || !placeholder) return

  card.toggleAttribute('hidden')
  placeholder.toggleAttribute('hidden')
  showMore?.toggleAttribute('hidden')
}

function removeAnnouncementCard(event_id: FormDataEntryValue) {
  const card = document.querySelector(`#feed-item-${event_id}`)?.parentElement
  const prevCard = card?.previousElementSibling
  const nextCard = card?.nextElementSibling

  if (!card) return

  card.addEventListener('transitionend', () => {
    card.remove()

    const totalCards = document.querySelectorAll('.js-pinned-announcement-container').length
    // Hide Show all/less if there are less than 3 announcements
    if (totalCards < 3) {
      document.querySelector('.js-pinned-announcements-toggle')?.toggleAttribute('hidden', true)
    }
    // Show "No new announcements yet" if there are no more pinned announcements
    if (!nextCard && !prevCard) {
      document.querySelector('#dismissed-announcements-placeholder')?.removeAttribute('hidden')
      return
    }

    if (card.classList.contains('Details-content--hidden')) {
      return
    } else if (nextCard) {
      slideAnnouncementCard(nextCard)
    }
  })
  card.classList.add('transition')
}

function slideAnnouncementCard(nextCard: Element) {
  if (nextCard.classList.contains('Details-content--hidden')) {
    nextCard.classList.remove('Details-content--hidden')
  } else {
    const nextNextCard = nextCard.nextElementSibling
    if (nextNextCard && nextNextCard.classList.contains('Details-content--hidden')) {
      nextNextCard.classList.remove('Details-content--hidden')
    }
  }
}

on('click', '.js-feed-disinterest-submit', function (e) {
  e.preventDefault()

  submitFeedDisinterest(e.currentTarget)
})

on('click', '.js-feed-hide-event-submit', function (e) {
  e.preventDefault()

  submitEventVisibility(e.currentTarget)
})

on('click', '.js-disinterest-checkbox', function (e) {
  const checkbox = e.currentTarget as HTMLInputElement
  const form = checkbox.closest('form')
  if (!form) return

  const submit = form.querySelector<HTMLButtonElement>('.js-feed-disinterest-submit')
  if (!submit) return

  const checkboxes = form.querySelectorAll<HTMLInputElement>('.js-disinterest-checkbox')
  // if all checkboxes are unchecked, disable the submit button
  submit.disabled = Array.from(checkboxes).every(cb => !cb.checked)
})

// hide details popups if the user scrolls through the sidebar
const leftColumnDetailsObserver = new IntersectionObserver(
  entries => {
    for (const entry of entries) {
      entry.target.toggleAttribute('open', false)
    }
  },
  {
    root: document.querySelector('.js-left-column-scroll-container'),
  },
)

observe('.js-left-column-scroll-container details', el => leftColumnDetailsObserver.observe(el))
